import type { AdminExerciseDetail } from '@steado/admin-contract/exercises';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { Panel } from '@/components/panel/panels';
import { createExercise } from '@/screens/exercises/exercises-query';

import { ExerciseFields } from './ExerciseFields';
import { EMPTY_EXERCISE_FORM, formIssues, toCreateRequest, type ExerciseFormValues } from './exercise-form';

export interface NewExerciseScreenProps {
  /** A name to start from: what the gyms wrote, opened from "From gyms". */
  readonly initialName?: string | undefined;
  /**
   * A whole form to start from: an exercise request's AI draft. Wins over
   * `initialName`. Read once, on mount, like any initial state.
   */
  readonly initialValues?: ExerciseFormValues | undefined;
  /** What the draft's checks dropped or flagged, shown above the form until it is created. */
  readonly draftWarnings?: readonly string[] | undefined;
  readonly onCreated: (exercise: AdminExerciseDetail) => void;
}

/**
 * Creating an exercise, on a page rather than in a modal.
 *
 * It was a modal, and a modal is why half the row could not be set from it: a
 * dialog sized for four fields quietly became the definition of what an
 * exercise had, and secondary muscles, sort order and the stills were simply
 * not there. The same fields the detail page edits are the fields this creates,
 * because it is literally the same component.
 *
 * Media is still not here, and that is not an omission: a clip or a still is an
 * object keyed by the exercise's id, and the id does not exist until the row
 * does. The page hands off to the new row's Media tab instead.
 */
export function NewExerciseScreen({
  initialName,
  initialValues,
  draftWarnings,
  onCreated,
}: NewExerciseScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const [values, setValues] = useState(
    () => initialValues ?? { ...EMPTY_EXERCISE_FORM, name: initialName ?? EMPTY_EXERCISE_FORM.name },
  );

  const create = useMutation({
    mutationFn: () => createExercise(api, toCreateRequest(values)),
    onSuccess: (exercise) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'exercises'] });
      onCreated(exercise);
    },
    retry: false,
  });

  const issues = formIssues(values);
  const blocked = issues.length > 0 || !can.mutate;

  return (
    <div className="flex flex-col gap-3.5">
      <section
        aria-label="New exercise"
        className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-border bg-card px-5 py-4"
      >
        <div className="flex flex-col gap-1.5">
          <h2 className="text-[17px] font-semibold text-foreground">New exercise</h2>
          <p className="text-[13px] text-muted-foreground">
            The slug is derived from the name and frozen after this. There is no field for it.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {create.isError ? (
            <span role="alert" className="max-w-[360px] text-[13px] text-err-fg">
              {create.error.message}
            </span>
          ) : null}
          <Button
            type="button"
            onClick={() => {
              if (!blocked) create.mutate();
            }}
            disabled={blocked || create.isPending}
            title={can.mutate ? (issues.length === 0 ? undefined : issues.join(' ')) : ADMIN_ONLY}
          >
            {create.isPending ? 'Creating…' : 'Create exercise'}
          </Button>
        </div>
      </section>

      {draftWarnings === undefined || draftWarnings.length === 0 ? null : (
        <Alert tone="warn" role="note">
          <AlertTitle>Filled in from the AI draft: check these before creating</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {draftWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {issues.length === 0 ? null : (
        <p className="rounded-xl border border-border bg-muted px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">
          {/* Stated rather than left to a disabled button: an operator should
              not have to guess which of ten fields is holding Create shut. */}
          Before this can be created: {issues.join(' ')}
        </p>
      )}

      <ExerciseFields values={values} onChange={setValues} disabled={!can.mutate} />

      <Panel title="Media">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The demo clip and the demonstration stills are uploaded from the exercise&apos;s own Media tab, once
          it exists. An object is keyed by the row&apos;s id, and there is no id to key against yet. Creating
          this takes you straight there.
        </p>
      </Panel>
    </div>
  );
}

const ADMIN_ONLY = 'Creating an exercise requires the admin role.';
